import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard, Button, Icon, StatusPill, Pagination } from '../../campaign-os';
import { usePageChrome } from '../PageChrome';
import { useApiData } from '../data/useApiData';
import { useAllVendors } from '../data/useAllVendors';
import { useVendorState, useLatestLinks, useRegistryStatus } from '../data/useVendorState';
import { useShellToast } from '../useShellToast';
import { useAuth } from '../../../../contexts/AuthContext';
import { throughVendorService, getErrorMessage } from '../../../../services/apiService';
import {
  SEGMENTS,
  segmentByKey,
  LINK_STATUS_PILL,
  LINK_VALID_DAYS,
  DELIVERY_PILL,
  DELIVERY_LABEL,
  batchStatusPill,
  batchActions,
} from '../data/throughVendor';
import { askByKey, baseHealth } from '../data/regularizationInsights';
import { checkByKey, toneFor } from '../data/selfRegularization';
import { formatDateTime } from '../data/format';
import { RegularizationModeTabs } from '../components/RegularizationModeTabs';
import { RegistryNotice } from '../components/SelfRegularizationBits';
import { BaseHealthStrip } from '../components/RegularizationInsights';
import { WhoNeedsAsking, sendLinkPath, useRemind } from '../components/WhoNeedsAsking';

/** Vendors of one send shown at a time when its row is open — a bulk send can reach a thousand. */
const LINKS_PER_PAGE = 30;

/**
 * Regularization through the vendor — the hub.
 *
 * The vendor fills the form, not the admin. This page answers three questions and nothing else:
 * where the vendor base stands, who needs asking, and what has already been sent. Choosing who
 * gets a link and what they are asked for happens in the wizard behind "Send the link", so this
 * page never has to be a form as well as a report.
 *
 * Live: a send mails each vendor one personal link (GET/POST /regularization/*). Each vendor's row
 * says whether that mail arrived (the provider's word, or the SMTP attempt); a mail that failed or
 * bounced is resent on the same link, an unanswered link gets a reminder, an expired one a fresh
 * link, and submitted answers go to review.
 */

const num = (n) => Number(n || 0).toLocaleString('en-IN');

/** A button inside the clickable row must not also toggle it. */
const stop = (fn) => (e) => {
  e.stopPropagation();
  fn();
};

/** One vendor in an expanded send: where the link stands, and — when it never arrived — why. */
function VendorLinkCard({ vendor: v }) {
  const lp = LINK_STATUS_PILL[v.status] || LINK_STATUS_PILL.sent;
  const dp = DELIVERY_PILL[v.delivery];
  const when = v.verified_at || v.submitted_at || v.reminded_at || v.sent_at;
  const quiet = [
    v.reminded_at && v.status === 'sent' ? `Reminded${v.reminder_count > 1 ? ` ×${v.reminder_count}` : ''}` : null,
    !dp && v.delivery ? DELIVERY_LABEL[v.delivery] : null,
  ].filter(Boolean);
  return (
    <div className={`rounded-lg border bg-white/70 px-3 py-2 ${dp ? 'border-cos-status-coral/40' : 'border-cos-line'}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold text-cos-ink">{v.company_name}</div>
          <div className="mono truncate text-2xs text-cos-ink-muted">{v.vendor_code || 'No code'} · {v.email || 'no email'}</div>
        </div>
        <div className="shrink-0 text-right">
          <StatusPill status={lp.pill} label={lp.label} />
          <div className="mt-0.5 text-2xs text-cos-ink-muted">
            {quiet.length > 0 && `${quiet.join(' · ')} · `}
            {formatDateTime(when)}
          </div>
        </div>
      </div>
      {dp && (
        <div className="mt-1.5 flex min-w-0 items-center gap-2 border-t border-cos-line-subtle pt-1.5" title={v.delivery_error || dp.label}>
          <StatusPill status={dp.pill} label={dp.label} className="shrink-0" />
          <span className="min-w-0 truncate text-2xs text-cos-status-coral">{v.delivery_error || 'No reason was given'}</span>
        </div>
      )}
    </div>
  );
}

/** One send, expandable into its vendors, where each of their links stands, and what can be done next. */
function BatchRow({ batch, open, onToggle, isAdmin, onRemind, reminding, onResend, resending, onReview, onFresh }) {
  const [vendorPage, setVendorPage] = useState(1);
  const seg = segmentByKey(batch.segment) || SEGMENTS[0];
  const c = batch.counts || {};
  const p = batchStatusPill(batch);
  const asks = (batch.asks || []).map(askByKey).filter(Boolean);
  const act = batchActions(batch);
  const anything = act.remind.length + act.resend.length + act.review + act.fresh.length > 0;
  const summary = [
    act.resend.length > 0 && `${num(act.resend.length)} never received the mail`,
    act.review > 0 && `${num(act.review)} answered, waiting for your review`,
    act.remind.length > 0 && `${num(act.remind.length)} still to answer`,
    act.fresh.length > 0 && `${num(act.fresh.length)} expired unanswered`,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <>
      <tr onClick={onToggle} className={`cursor-pointer border-b border-cos-line-subtle transition-colors ${open ? 'bg-white/60' : 'hover:bg-white/50'}`}>
        <td className="w-8 px-4 py-3 text-cos-ink-muted">
          <Icon name={open ? 'expand_more' : 'chevron_right'} className="text-[18px]" />
        </td>
        <td className="px-3 py-3">
          <div className="font-semibold text-cos-ink">{formatDateTime(batch.created_at)}</div>
          <div className="mono text-2xs text-cos-ink-muted">#{batch.id} · {batch.created_by?.full_name || 'Admin'}</div>
        </td>
        <td className="px-3 py-3">
          <div className="flex items-center gap-1 text-[12px] font-medium text-cos-ink">
            <Icon name={seg.icon} className="text-[15px] text-cos-teal-deep" />
            {seg.label}
          </div>
          {asks.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {asks.map((a) => {
                const t = toneFor(a.key);
                return (
                  <span key={a.key} className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-2xs font-semibold ${t.bg} ${t.text}`}>
                    <Icon name={a.icon} className="text-[11px]" />
                    {a.short}
                  </span>
                );
              })}
            </div>
          )}
        </td>
        <td className="mono px-3 py-3 text-right text-cos-ink">{num(c.total)}</td>
        <td className="px-3 py-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px]">
            <span className="text-cos-ink-secondary"><span className="mono font-semibold text-cos-teal-deep">{num(c.verified)}</span> verified</span>
            {c.submitted > 0 && <span className="text-cos-ink-secondary"><span className="mono font-semibold text-cos-status-amber">{num(c.submitted)}</span> submitted</span>}
            {c.sent > 0 && <span className="text-cos-ink-muted"><span className="mono font-semibold">{num(c.sent)}</span> waiting</span>}
            {c.expired > 0 && <span className="text-cos-ink-muted"><span className="mono font-semibold">{num(c.expired)}</span> expired</span>}
            {c.undelivered > 0 && (
              <span className="inline-flex items-center gap-0.5 font-semibold text-cos-status-coral" title="The mail carrying the link failed at the provider or bounced. Open the row to see why and resend.">
                <Icon name="error" className="text-[14px]" />
                <span className="mono">{num(c.undelivered)}</span> not delivered
              </span>
            )}
          </div>
        </td>
        <td className="px-3 py-3"><StatusPill status={p.pill} label={p.label} /></td>
      </tr>
      {open && (
        <tr className="border-b border-cos-line-subtle bg-[rgba(250,249,244,0.6)]">
          <td colSpan={6} className="px-4 py-3">
            {isAdmin && anything && (
              <div
                className={`mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 ${
                  act.resend.length > 0 ? 'border-cos-status-coral/30 bg-cos-status-coral-bg/60' : 'border-cos-status-amber/30 bg-cos-status-amber-bg/60'
                }`}
              >
                <span className="text-[12px] text-cos-ink">{summary}.</span>
                <div className="flex flex-wrap items-center gap-1.5">
                  {act.review > 0 && (
                    <Button variant="primary" size="sm" icon="rate_review" onClick={stop(onReview)} title="Compare each answer with the record, then approve it into the record">
                      Review {num(act.review)} submitted
                    </Button>
                  )}
                  {act.resend.length > 0 && (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={resending ? 'hourglass_top' : 'forward_to_inbox'}
                      disabled={resending}
                      onClick={stop(() => onResend(act.resend))}
                      title="The same link, mailed again to the vendors whose mail failed or bounced"
                    >
                      {resending ? 'Resending…' : `Resend ${num(act.resend.length)}`}
                    </Button>
                  )}
                  {act.remind.length > 0 && (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={reminding ? 'hourglass_top' : 'notifications_active'}
                      disabled={reminding}
                      onClick={stop(() => onRemind(act.remind))}
                    >
                      {reminding ? 'Reminding…' : `Remind ${num(act.remind.length)}`}
                    </Button>
                  )}
                  {act.fresh.length > 0 && (
                    <Button variant="secondary" size="sm" icon="replay" onClick={stop(() => onFresh(act.fresh))} title="Opens the send with these vendors and the same asks chosen">
                      Send a fresh link to {num(act.fresh.length)}
                    </Button>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2 xl:grid-cols-3">
              {batch.vendors.slice((vendorPage - 1) * LINKS_PER_PAGE, vendorPage * LINKS_PER_PAGE).map((v) => (
                <VendorLinkCard key={v.vendor_id} vendor={v} />
              ))}
            </div>
            {batch.vendors.length > LINKS_PER_PAGE && (
              <div onClick={(e) => e.stopPropagation()}>
                <Pagination
                  className="mt-3"
                  page={Math.min(vendorPage, Math.ceil(batch.vendors.length / LINKS_PER_PAGE))}
                  pageSize={LINKS_PER_PAGE}
                  total={batch.vendors.length}
                  onPageChange={setVendorPage}
                  noun="vendors"
                />
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export function RegularizationPage() {
  const navigate = useNavigate();
  const toast = useShellToast();
  const { isAdmin } = useAuth();

  const all = useAllVendors();
  const base = all.vendors || [];
  const batches = useApiData(() => throughVendorService.listBatches({ limit: 100 }), [], { cacheKey: 'reg:batches:100' });
  const batchRows = useMemo(() => batches.data?.data || [], [batches.data]);
  const registry = useRegistryStatus();
  const vendorState = useVendorState();
  const links = useLatestLinks();

  const [openBatch, setOpenBatch] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [howOpen, setHowOpen] = useState(false);

  const refresh = () => {
    batches.refetch();
    links.refetch();
  };
  const { busyKey: remindingKey, remind } = useRemind(refresh);

  // Answers arrive as vendors submit — over hours and days — and the provider's word on each mail
  // within minutes; while any link is open a slow poll keeps the list honest.
  const anyOpen = batchRows.some((b) => (b.counts?.sent || 0) > 0);
  useEffect(() => {
    if (!anyOpen) return undefined;
    const t = setInterval(() => {
      batches.refetch();
      links.refetch();
    }, 30_000);
    return () => clearInterval(t);
  }, [anyOpen, batches.refetch, links.refetch]);

  const stateOf = vendorState.byId;
  const ready = base.length > 0 && !vendorState.loading && !links.loading;

  const health = useMemo(() => (ready ? baseHealth(base, vendorState.stateOf) : null), [ready, base, vendorState.stateOf]);

  /** Straight into the send, with these vendors and this ask already chosen. */
  const sendFor = (vendorIds, askKeys) => navigate(sendLinkPath(vendorIds, askKeys));

  /** The same link again, to vendors in this send whose mail failed or bounced. */
  const resend = async (batch, vendorIds) => {
    setResendingId(batch.id);
    try {
      const res = await throughVendorService.resend({ batchId: batch.id, vendor_ids: vendorIds });
      const d = res.data || {};
      const parts = [`Mail resent to ${num(d.resent)} vendor${d.resent === 1 ? '' : 's'}`];
      if (d.failed?.length) parts.push(`${num(d.failed.length)} failed again (${[...new Set(d.failed.map((f) => f.reason))].join('; ')})`);
      if (d.skipped?.length) parts.push(`${num(d.skipped.length)} skipped (${[...new Set(d.skipped.map((s) => s.reason))].join('; ')})`);
      toast(parts.join(' · '), { tone: d.failed?.length ? 'warning' : d.resent ? 'success' : 'info' });
      refresh();
    } catch (err) {
      toast(getErrorMessage(err) || 'Could not resend the mail', { tone: 'error' });
    } finally {
      setResendingId(null);
    }
  };

  /** A number on the health strip opens the send, already narrowed to those vendors. */
  const pickHealth = (kind, checkKey) => {
    const c = checkByKey(checkKey);
    if (kind === 'missing') {
      const ids = base.filter((v) => !(stateOf.get(v.id)?.inputs || []).includes(c.needs)).map((v) => v.id);
      if (ids.length) sendFor(ids, [c.key]);
      return;
    }
    // Already on record but never confirmed — that is a registry check, not something to ask for.
    navigate(`/campaigning-tool/regularization/self/new?checks=${c.key}&need=unverified:${c.key}`);
  };

  usePageChrome(
    () => ({
      title: 'Vendor Regularization',
      subtitle: 'Send each vendor a link to the form — they fill it in, the form verifies it, you approve',
      actions: isAdmin ? (
        <Button variant="primary" icon="send" onClick={() => navigate('/campaigning-tool/regularization/send')}>
          Send the link
        </Button>
      ) : (
        <Button variant="secondary" icon="lock" disabled title="Only an admin can send regularization links">
          Admin sends links
        </Button>
      ),
    }),
    [isAdmin, navigate]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <RegularizationModeTabs mode="vendor" />
        <button type="button" onClick={() => setHowOpen((o) => !o)} className="inline-flex items-center gap-1 text-[12px] font-semibold text-cos-teal-deep hover:underline">
          <Icon name="help" className="text-[15px]" /> How this works
        </button>
      </div>

      {howOpen && (
        <GlassCard accentRail>
          <p className="text-[13px] leading-[19px] text-cos-ink-secondary">
            <span className="font-semibold text-cos-ink">The vendor fills the form, not you.</span> Pick the vendors, choose what they
            are asked for — GST, PAN, bank, MSME, documents, or the whole form — and send. Each gets one email with a personal link,
            valid {LINK_VALID_DAYS} days, that opens on just those sections. The form verifies what they enter; nothing changes on a
            record until you approve the submission. A mail that never arrives is resent on the same link; unanswered links get a
            reminder; expired ones a fresh link.
          </p>
        </GlassCard>
      )}

      <RegistryNotice status={registry} />

      <BaseHealthStrip health={health} loading={(all.loading && !all.vendors) || vendorState.loading} onPick={pickHealth} />

      <WhoNeedsAsking base={base} stateOf={vendorState.stateOf} links={links} ready={ready} disabled={!isAdmin} onReminded={refresh} />

      {/* ------------------------------------------------------------ sends so far */}
      <GlassCard padded={false}>
        <div className="flex flex-wrap items-end justify-between gap-3 px-5 pt-5">
          <div>
            <h3 className="text-[14px] font-bold tracking-tight text-cos-ink">Links sent</h3>
            <p className="mt-0.5 text-[12px] text-cos-ink-secondary">Every send, what it asked for, whether the mail arrived, and where each vendor's link stands. Open a row for the vendors.</p>
          </div>
          {anyOpen && (
            <span className="inline-flex items-center gap-1.5 text-[12px] text-cos-teal-deep">
              <span className="h-2 w-2 animate-pulse rounded-full bg-cos-teal" /> Answers arriving
            </span>
          )}
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cos-line">
                <th className="w-8 px-4 py-2.5" />
                {['Sent', 'To whom, for what', 'Vendors', 'Where it stands', 'Status'].map((h, i) => (
                  <th key={h} className={`whitespace-nowrap px-3 py-2.5 text-2xs font-bold uppercase tracking-[0.08em] text-cos-ink-muted ${i === 2 ? 'text-right' : 'text-left'}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {batchRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-cos-ink-muted">
                      <Icon name="forward_to_inbox" className="text-[28px]" />
                      <span className="text-sm">No links sent yet. Take a suggestion above, or send the link.</span>
                    </div>
                  </td>
                </tr>
              ) : (
                batchRows.map((b) => (
                  <BatchRow
                    key={b.id}
                    batch={b}
                    open={openBatch === b.id}
                    onToggle={() => setOpenBatch(openBatch === b.id ? null : b.id)}
                    isAdmin={isAdmin}
                    reminding={remindingKey === `batch:${b.id}`}
                    onRemind={(ids) => remind(`batch:${b.id}`, ids)}
                    resending={resendingId === b.id}
                    onResend={(ids) => resend(b, ids)}
                    onReview={() => navigate(`/vendor-regularization/batch/${b.id}`)}
                    onFresh={(ids) => sendFor(ids, b.asks)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  );
}

export default RegularizationPage;
